import { parseQuality } from './quality';

/**
 * URL-level localization: which languages the site serves, and how those
 * languages appear in a path.
 *
 * `supportedLocales` is the single source of truth. Adding a language means
 * adding it here (and its i18n file + content). Routing, prerender, sitemap
 * and head tags all iterate this list — none of them name a language.
 *
 * The first entry is the default, unprefixed locale. Every other locale is
 * served under `/{locale}/…`. There is no `/en/…` prefix, on purpose: the
 * English URLs that already exist stay exactly as they are.
 */

export const supportedLocales = ['en', 'es'] as const;
export type Locale = (typeof supportedLocales)[number];

export const defaultLocale: Locale = supportedLocales[0];

/**
 * Open Graph `language_TERRITORY` tags. A locale without an explicit
 * territory falls back to the uppercased language code (`fr` → `fr_FR`).
 */
const ogTerritory = {
  en: 'US',
  es: 'ES',
} as const satisfies Record<Locale, string>;

export function isSupportedLocale(value: string | undefined | null): value is Locale {
  return Boolean(value && (supportedLocales as readonly string[]).includes(value));
}

/**
 * The locale a language tag refers to. `es-MX` and `es-419` are Spanish;
 * anything unknown falls back to the default rather than inventing a page.
 */
export function resolveLocale(lang: string | undefined | null): Locale {
  if (!lang) return defaultLocale;
  const base = lang.toLowerCase().split('-')[0];
  return isSupportedLocale(base) ? base : defaultLocale;
}

export function isDefaultLocale(locale: string): boolean {
  return locale === defaultLocale;
}

/** Filename suffix for a non-default locale artifact (`llms.es.txt`). Default locale is unsuffixed. */
export function localeArtifactSuffix(locale: string, defaultLoc: string = defaultLocale): string {
  return locale === defaultLoc ? '' : `.${locale}`;
}

/** `''` for the default locale, `'/es'` for every other. */
export function localePathPrefix(locale: string): string {
  return isDefaultLocale(locale) ? '' : `/${locale}`;
}

export function ogLocaleFor(locale: string): string {
  const territory = (ogTerritory as Record<string, string>)[locale] ?? locale.toUpperCase();
  return `${locale}_${territory}`;
}

/**
 * Strip a locale prefix (and a trailing slash) so `/es/product/` and
 * `/product` both become `{ locale: 'es' | 'en', pathname: '/product' }`.
 *
 * A leading segment is only a locale when it is a *non-default* entry of
 * `supportedLocales`. `/en/product` is therefore not a localized URL — the
 * default locale has no prefix, and inventing one would fork every English
 * canonical.
 */
export function localeFromPathname(pathname: string): { locale: Locale; pathname: string; prefix: string } {
  const withLeading = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const trimmed = withLeading.length > 1 && withLeading.endsWith('/') ? withLeading.slice(0, -1) : withLeading;
  const segments = trimmed.split('/');
  const maybeLocale = segments[1];
  if (isSupportedLocale(maybeLocale) && !isDefaultLocale(maybeLocale)) {
    const rest = `/${segments.slice(2).join('/')}`;
    return {
      locale: maybeLocale,
      pathname: rest === '/' ? '/' : rest,
      prefix: `/${maybeLocale}`,
    };
  }
  return { locale: defaultLocale, pathname: trimmed || '/', prefix: '' };
}

/**
 * The URL this page lives at in `locale`. Accepts either an unprefixed path
 * or an already-prefixed one; the prefix is stripped first so the helper is
 * safe to call twice.
 */
export function localizePath(path: string, locale: string): string {
  const unprefixed = localeFromPathname(path).pathname;
  if (isDefaultLocale(locale)) return unprefixed;
  return unprefixed === '/' ? `/${locale}` : `/${locale}${unprefixed}`;
}

/**
 * A page URL that names its language explicitly. Use on language-switch links
 * so `languageRedirectTarget` honors the choice instead of re-negotiating
 * from Accept-Language or `navigator.languages` (bare `/` back to `/es`).
 */
export function explicitLanguagePath(path: string, locale: Locale): string {
  return `${localizePath(path, locale)}?lang=${locale}`;
}

/**
 * The language switch's target: the same page in `locale`, keeping the
 * visitor's other query parameters and #fragment, with `lang` set explicitly.
 */
export function languageSwitchHref(path: string, locale: Locale, search = '', hash = ''): string {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  params.delete('lang');
  params.set('lang', locale);
  const fragment = hash && hash !== '#' ? (hash.startsWith('#') ? hash : `#${hash}`) : '';
  return `${localizePath(path, locale)}?${params.toString()}${fragment}`;
}

export function absoluteUrl(path: string, siteUrl: string): string {
  const localized = path === '/' ? '/' : path;
  return `${siteUrl}${localized}`;
}

/**
 * Every language variant of one page, plus `x-default` pointing at the
 * default-locale URL. Used for `<link rel="alternate" hreflang>` and the
 * sitemap's `xhtml:link` annotations.
 *
 * `locales` is a parameter so a test can pass a synthetic language without
 * editing `supportedLocales` — production callers omit it.
 */
export function hreflangAlternates(
  unprefixedPath: string,
  siteUrl: string,
  locales: readonly string[] = supportedLocales,
  defaultLoc: string = defaultLocale,
): Array<{ hreflang: string; href: string }> {
  const path = localeFromPathname(unprefixedPath).pathname;
  const links = locales.map((locale) => ({
    hreflang: locale,
    href: absoluteUrl(localizePath(path, locale), siteUrl),
  }));
  links.push({
    hreflang: 'x-default',
    href: absoluteUrl(localizePath(path, defaultLoc), siteUrl),
  });
  return links;
}

/** Every localized URL for one unprefixed path, default locale first. */
export function allLocalePaths(unprefixedPath: string, locales: readonly string[] = supportedLocales): string[] {
  const path = localeFromPathname(unprefixedPath).pathname;
  return locales.map((locale) => localizePath(path, locale));
}

/**
 * Paths that are files or Worker-owned routes, not prerendered pages. A
 * language prefix on `/images/…` or `/sitemap.xml` would 404.
 */
const PASSTHROUGH_PREFIXES = ['/images/', '/assets/', '/api/', '/auth/', '/admin/'];
const PASSTHROUGH_EXACT = new Set(['/sitemap.xml', '/robots.txt', '/api', '/auth', '/admin']);

function isPassthroughPath(path: string): boolean {
  if (PASSTHROUGH_EXACT.has(path)) return true;
  if (PASSTHROUGH_PREFIXES.some((prefix) => path.startsWith(prefix))) return true;
  // A file with an extension (`logo.svg`, `llms.txt`) is not a page. `.html`
  // is the exception the prerenderer itself writes.
  return /\.[a-z0-9]+$/i.test(path) && !path.endsWith('.html');
}

/**
 * The href a visitor on `locale` should follow. Page paths pick up the
 * locale prefix; assets, APIs and already-prefixed URLs are left alone.
 * Query strings and hashes survive.
 */
export function localizeHref(href: string, locale: string): string {
  if (!href.startsWith('/') || href.startsWith('//')) return href;
  const split = href.search(/[?#]/);
  const path = split === -1 ? href : href.slice(0, split);
  const rest = split === -1 ? '' : href.slice(split);
  if (isDefaultLocale(locale) || isPassthroughPath(path)) return href;
  if (localeFromPathname(path).locale !== defaultLocale) return href;
  return `${localizePath(path, locale)}${rest}`;
}

/** Rewrite root-relative `href` attributes in rendered HTML for `locale`. */
export function localizeHtmlHrefs(html: string, locale: string): string {
  if (isDefaultLocale(locale)) return html;
  return html.replace(/href=(["'])(\/[^"']*)\1/g, (_full, quote: string, href: string) => {
    return `href=${quote}${localizeHref(href, locale)}${quote}`;
  });
}

/** Rewrite root-relative Markdown targets (`](/docs/…)`) for `locale`. */
export function localizeMarkdownHrefs(markdown: string, locale: string): string {
  if (isDefaultLocale(locale)) return markdown;
  return (
    markdown
      // Inline destinations: `](/docs/x)`.
      .replace(/\]\((\/(?!\/)[^)]+)\)/g, (_match, href: string) => `](${localizeHref(href, locale)})`)
      // Reference definitions: `[g]: /docs/x` or `[g]: </docs/x>` (a title may follow).
      .replace(
        /^( {0,3}\[[^\]\n]+\]:[ \t]*)(<)?(\/(?!\/)[^\s>]*)(>)?/gm,
        (_match, head: string, open: string | undefined, href: string, close: string | undefined) =>
          `${head}${open ?? ''}${localizeHref(href, locale)}${close ?? ''}`,
      )
  );
}

/**
 * An explicit `?lang=` request, or null when there is none.
 *
 * Exact match against `supportedLocales` only (`es`, not `es-MX`): a value
 * that is not a served locale falls through to Accept-Language / navigator
 * detection rather than half-matching. This is how a link can name its own
 * language (the language switcher uses it, see `explicitLanguagePath`).
 */
export function requestedLanguage(search: string): Locale | null {
  const query = search.startsWith('?') || search.length === 0 ? search : `?${search}`;
  const value = new URLSearchParams(query).get('lang')?.toLowerCase();
  return isSupportedLocale(value) ? value : null;
}

interface LanguageRange {
  range: string;
  quality: number;
  index: number;
}

function parseAcceptLanguage(header: string): LanguageRange[] {
  const ranges: LanguageRange[] = [];
  for (const [index, part] of header.split(',').entries()) {
    const [rawRange, ...parameters] = part.split(';');
    const range = rawRange?.trim().toLowerCase();
    if (!range || !/^(\*|[a-z]{1,8}(-[a-z0-9]{1,8})*)$/.test(range)) continue;
    const quality = parseQuality(parameters);
    if (quality === null) continue; // a malformed weight discards the range
    ranges.push({ range, quality, index });
  }
  return ranges;
}

/**
 * Best supported locale in an `Accept-Language` header (RFC 9110 §12.5.4,
 * matching in the spirit of RFC 4647).
 *
 * Each supported locale gets the quality of the most specific range that
 * names it: the exact range (`es`), else the best regional range (`es-MX`
 * counts as Spanish), else the wildcard `*`, which stands for every language
 * not named elsewhere. `q=0` means "not acceptable", so `en;q=0, *` excludes
 * English and the wildcard picks Spanish. The highest quality wins; ties go
 * to the range listed first, and between locales reached only through `*`,
 * to the default (English). Nothing acceptable — including an empty header —
 * is English.
 */
export function localeFromAcceptLanguage(header: string | null | undefined): Locale {
  const ranges = parseAcceptLanguage(header ?? '');
  const wildcard = ranges.find(({ range }) => range === '*');
  let best: { locale: Locale; quality: number; index: number } | null = null;
  for (const locale of supportedLocales) {
    const exact = ranges.find(({ range }) => range === locale);
    const regional = ranges
      .filter(({ range }) => range.startsWith(`${locale}-`))
      .sort((a, b) => b.quality - a.quality || a.index - b.index)[0];
    const match = exact ?? regional ?? wildcard;
    if (!match || match.quality <= 0) continue;
    if (!best || match.quality > best.quality || (match.quality === best.quality && match.index < best.index)) {
      best = { locale, quality: match.quality, index: match.index };
    }
  }
  return best?.locale ?? defaultLocale;
}

/**
 * Where this request should be sent so the URL and the language agree, or
 * null when the URL should be served as-is.
 *
 * A prefixed path (`/es/…`) already names a locale. Browser language never
 * moves it: a direct link, a search result and an hreflang alternate stay
 * where they were opened. `?lang=` is the other explicit signal. It wins
 * over both the path and detection, and when it already matches the path
 * detection is not applied either — `/product?lang=en` must not bounce to
 * `/es/product` and straight back.
 *
 * The unprefixed URL is the entry that negotiates. Detection choosing
 * another supported locale redirects there; detection choosing English
 * serves this URL. Assets, APIs and files are not pages, so they never
 * redirect. The query string rides along unchanged, `lang` included, which
 * is what stops `/es/product?lang=en` → `/product?lang=en` from being
 * negotiated back to Spanish.
 */
/**
 * The canonical form of a page path, or null when it already is canonical:
 * `/index`, `/index.html` → `/` (and `/es/index…` → `/es`), `/product.html` →
 * `/product`, `/product/` → `/product`. Files with other extensions and
 * Worker-owned paths are not pages and are left alone.
 */
export function canonicalPagePath(pathname: string): string | null {
  if (pathname === '/' || isPassthroughPath(pathname)) return null;
  let path = pathname.length > 1 && pathname.endsWith('/') ? pathname.replace(/\/+$/, '') || '/' : pathname;
  if (path.endsWith('.html')) path = path.slice(0, -'.html'.length) || '/';
  const { prefix, pathname: unprefixed } = localeFromPathname(path);
  if (unprefixed === '/index') path = prefix || '/';
  return path === pathname ? null : path;
}

/**
 * Whether the answer for this URL depends on Accept-Language: an unprefixed
 * page with no explicit `?lang=`. Such responses must say `Vary: Accept-Language`.
 */
export function negotiatesLanguage(pathname: string, search: string): boolean {
  if (isPassthroughPath(pathname)) return false;
  if (requestedLanguage(search)) return false;
  return isDefaultLocale(localeFromPathname(pathname).locale);
}

export function languageRedirectTarget(pathname: string, search: string, detected: Locale): string | null {
  const rawPath = pathname.split('?')[0] ?? pathname;
  if (isPassthroughPath(rawPath)) return null;
  // Aliases (/index.html, /product/, …) are negotiated as the page they stand for.
  const pathOnly = canonicalPagePath(rawPath) ?? rawPath;

  const { locale: urlLocale, pathname: unprefixed } = localeFromPathname(pathOnly);
  const query = search.startsWith('?') || search.length === 0 ? search : `?${search}`;
  const requested = requestedLanguage(query);

  let next: Locale | null = null;
  if (requested) {
    next = requested === urlLocale ? null : requested;
  } else if (isDefaultLocale(urlLocale) && !isDefaultLocale(detected)) {
    next = detected;
  }
  if (!next) return null;

  const target = `${localizePath(unprefixed, next)}${query}`;
  const current = `${rawPath}${query}`;
  return target === current ? null : target;
}
